/**
 * OMG https://mantine.dev/combobox/?e=BasicSelect
 */
import {
  CSSProperties,
  ReactNode,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Combobox,
  PillsInput,
  ScrollArea,
  useVirtualizedCombobox,
} from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import type { core } from 'zod';

import { Checkbox } from '../checkbox/Checkbox';
import { ClearButton } from '../clearbutton/ClearButton';
import { FormLabel } from '../formlabel/FormLabel';
import { LoadingIndicator } from '../loading-indicator/LoadingIndicator';

import { OPTION_HEIGHT, OPTION_LIST_PADDING } from './helpers/constants';
import { sortSelected } from './helpers/sortSelected';
import {
  DynamicWidthSettings,
  useDynamicWidth,
} from './helpers/useDynamicWidth';
import { FormError, SelectIcon } from './Form';
import { SelectionPillGroup, SelectionPills } from './SelectionPills';
import { SelectOption } from './SelectOption';

import styles from '../input/input.module.scss';
import dropdownStyles from './dropdown.module.scss';
import pillGroupStyles from './pill-group.module.scss';

export type MultiSelectOption<
  Data extends core.util.Primitive | unknown = unknown,
> = {
  value: Data;
  label?: string;
};

const DEFAULT_SELECT_ALL_LIMITS = {
  min: 2,
  max: Number.MAX_SAFE_INTEGER,
} as const;

export type MultiSelectProps<Data extends core.util.Primitive> = {
  className?: string;
  /** Controlled state onChange handler */
  onChange?: (value: Data[]) => void;
  /** Called when changing selection is done (on blur) */
  onCommit?: (value: Data[]) => void;
  /** Selection for controlled state */
  value?: Data[];
  /** Initial selection for uncontrolled state */
  defaultValue?: Data[];
  /** max height of dropdown scrollcontainer */
  dropdownHeight?: number | string;
  dynamicWidthSettings?: DynamicWidthSettings;
  /** maximum number of tags to show inline [1,n] */
  maxVisibleTags?: number;
  /** visible option count range in which the select-all action is shown */
  selectAllLimits?: {
    min?: number;
    max?: number;
  };
  /** add clear action if selection is not empty. Default: true */
  showClearAction?: boolean;
  /** disable clear action. Default: false */
  disableClearAction?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  error?: string;
  description?: ReactNode | ReactNode[];
  title?: string;
  inlineError?: boolean;
  data: MultiSelectOption<Data>[];
  label?: ReactNode | ReactNode[];
  loading?: boolean;
  placeholder?: string;
  required?: boolean;
  style?: CSSProperties;
  withinPortal?: boolean;
  sortCallback?: (
    a: MultiSelectOption<Data>,
    b: MultiSelectOption<Data>
  ) => number;
};

function arrayHasChanged(a: unknown[], b: unknown[]) {
  return a.length !== b.length || a.some((v) => !b.includes(v));
}

export function MultiSelect<Data extends core.util.Primitive>({
  className,
  data = [],
  value: selectionControlled,
  defaultValue = [],
  dynamicWidthSettings,
  placeholder,
  error,
  description,
  inlineError,
  onChange,
  label,
  loading,
  required,
  disabled,
  readOnly = false,
  onCommit,
  style,
  title,
  withinPortal = true,
  showClearAction = true,
  disableClearAction = false,
  maxVisibleTags = 2,
  selectAllLimits = DEFAULT_SELECT_ALL_LIMITS,
  dropdownHeight = '35vh',
  sortCallback,
}: MultiSelectProps<Data>) {
  maxVisibleTags = Math.max(maxVisibleTags, 1);
  const selectAllMinItems =
    selectAllLimits.min ?? DEFAULT_SELECT_ALL_LIMITS.min;
  const selectAllMaxItems =
    selectAllLimits.max ?? DEFAULT_SELECT_ALL_LIMITS.max;
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [hasFocus, setHasFocus] = useState(false);
  const [sortedOptions, setSortedOptions] = useState<MultiSelectOption<Data>[]>(
    []
  );
  const [opened, setOpened] = useState(false);
  const [scrollParent, setScrollParent] = useState<HTMLDivElement | null>(null);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(-1);

  const { t } = useTranslation();
  const id = useId();
  const [search, setSearch] = useState('');
  const [selection, setSelection] = useUncontrolled({
    value: selectionControlled,
    defaultValue,
    onChange,
  });

  const [currentSelection, setCurrentSelection] = useState<null | string[]>();
  function commitValue(value: Data[]) {
    if (currentSelection == null || arrayHasChanged(currentSelection, value)) {
      setCurrentSelection(null);
      onCommit?.(value);
    }
    setSearch('');
  }

  const handleValueSelect = (val: string) => {
    const selected = data.find(
      (d) => d.value === val || String(d.value) === val
    );

    const nextSelection = selection.some((s) => s === val || String(s) === val)
      ? selection.filter((v) => String(v) !== val)
      : // eslint-disable-next-line unicorn/prefer-spread
        [...selection].concat(selected != null ? [selected.value] : []);

    setSelection(nextSelection);
  };
  const handleValueRemove = (val?: Data) => {
    if (val == null || !selection.includes(val)) {
      return;
    }
    const nextSelection = selection.filter((v) => v !== val);
    setSelection(nextSelection);
  };

  const selectedLabels = selection.flatMap((item) => {
    if (item == null) {
      return [];
    }
    const label =
      data.find((option) => option.value === item)?.label ?? String(item);
    return [label];
  });

  const visibleSelection = useMemo(
    () =>
      sortedOptions.filter((item) =>
        (item.label ?? String(item.value))
          .toLowerCase()
          .includes(search.trim().toLowerCase())
      ),
    [sortedOptions, search]
  );

  const virtualizer = useVirtualizer({
    count: visibleSelection.length,
    getScrollElement: () => scrollParent,
    estimateSize: () => OPTION_HEIGHT,
    overscan: 5,
  });

  function onOptionSubmit(index: number) {
    const item = visibleSelection[index];
    if (item) {
      handleValueSelect(String(item.value));
    }
  }

  const combobox = useVirtualizedCombobox({
    opened,
    onOpenedChange: (isOpen) => {
      setOpened(isOpen);
      if (!isOpen) {
        setHasFocus(false);
        commitValue(selection);
      }
    },
    onDropdownOpen: () => {
      setCurrentSelection(selection.map(String));
      const sorted = [...data];
      if (sortCallback) {
        sorted.sort(sortCallback);
      } else {
        sorted.sort((a, b) => sortSelected(a, b, selection));
      }
      setSortedOptions(sorted);
      setHasFocus(true);
      // always scroll to top, to avoid blank screen after data refresh
      virtualizer.scrollToIndex(0, { align: 'end' });
    },
    totalOptionsCount: visibleSelection.length,
    getOptionId: (index) => `${id}-option-${index}`,
    selectedOptionIndex,
    setSelectedOptionIndex: (index) => {
      setSelectedOptionIndex(index);
      if (index !== -1) {
        virtualizer.scrollToIndex(index, { align: 'auto' });
      }
    },
    onSelectedOptionSubmit: onOptionSubmit,
  });

  const width = useDynamicWidth(
    dynamicWidthSettings,
    visibleSelection.map((item) => item.label ?? String(item.value))
  );

  let rightSection: ReactNode | undefined;
  let largeRightSection = false;

  if (loading) {
    rightSection = <LoadingIndicator data-testid="loading-indicator" />;
  } else {
    const isClearActionActive =
      search === '' &&
      !opened &&
      !(loading || disabled || readOnly) &&
      selection.length > 0;
    largeRightSection = showClearAction;
    rightSection = (
      <>
        {showClearAction && (
          <ClearButton
            hidden={!isClearActionActive}
            disabled={disableClearAction || disabled}
            loading={loading}
            onClear={() => {
              if (selection.length > 0) {
                setSelection([]);
                commitValue([]);
              }
            }}
          />
        )}
        <SelectIcon
          disabled={loading || disabled || readOnly}
          error={!!error}
        />
      </>
    );
  }

  return (
    <div
      title={title}
      ref={dropdownRef}
      className={className}
      style={style}
      data-testid="multi-select"
    >
      <Combobox
        position="bottom-start"
        width={width}
        store={combobox}
        disabled={disabled || loading || readOnly}
        resetSelectionOnOptionHover={false}
        portalProps={{
          target:
            !withinPortal && dropdownRef.current
              ? dropdownRef.current
              : undefined,
        }}
        classNames={{
          footer: dropdownStyles['dropdown__footer'],
          dropdown: dropdownStyles['dropdown'],
        }}
      >
        <FormLabel
          description={description}
          id={id}
          error={error}
          loading={loading}
          inline={inlineError}
          label={label}
          required={required}
          disabled={disabled === true}
        />
        <Combobox.DropdownTarget>
          {/* wrapper around label and input-like element, wrapping html-input and pills */}
          <PillsInput
            classNames={{
              wrapper: clsx(
                styles['wrapper'],
                largeRightSection && styles['wrapper--large-right-section']
              ),
              error: styles['error'],
              input: clsx(
                styles['input'],
                hasFocus && styles['input--is-focussed'],
                loading && styles['input--is-loading'],
                error && styles['input--error']
              ),
              section: styles['section'],
            }}
            disabled={loading || disabled || readOnly}
            error={error && <FormError error={error} inline={inlineError} />}
            id={id}
            onClick={() => combobox.openDropdown()}
            rightSection={rightSection}
            rightSectionPointerEvents="none"
            unstyled
          >
            <SelectionPillGroup>
              {search === '' && (
                <SelectionPills
                  labels={selectedLabels}
                  maxVisibleTags={maxVisibleTags}
                  disabled={disabled || loading}
                />
              )}
              <Combobox.EventsTarget>
                {/* actual html-input element inlined after pills */}
                <PillsInput.Field
                  type={selection.length === 0 ? 'visible' : 'auto'}
                  className={pillGroupStyles['pill--input']}
                  data-testid="search"
                  onFocus={() => combobox.openDropdown()}
                  onBlur={() => {
                    combobox.closeDropdown();
                    setSearch('');
                  }}
                  value={search}
                  placeholder={selection.length === 0 ? placeholder : undefined}
                  onChange={(event) => {
                    setSearch(event.currentTarget.value);
                    setSelectedOptionIndex(-1);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Backspace' && search.length === 0) {
                      event.preventDefault();
                      handleValueRemove(selection.at(-1));
                    }
                  }}
                />
              </Combobox.EventsTarget>
            </SelectionPillGroup>
          </PillsInput>
        </Combobox.DropdownTarget>
        <Combobox.Dropdown>
          <Combobox.Options style={{ width: style?.width }}>
            {visibleSelection.length > 0 ? (
              <ScrollArea.Autosize
                mah={dropdownHeight}
                type="scroll"
                scrollbarSize={4}
                viewportRef={setScrollParent}
                onMouseDown={(event) => event.preventDefault()}
              >
                <div
                  style={{
                    height:
                      // refer ./helpers/constants.ts for OPTION_LIST_PADDING usage
                      virtualizer.getTotalSize() + OPTION_LIST_PADDING * 2,
                    position: 'relative',
                  }}
                >
                  {virtualizer.getVirtualItems().map((virtualItem) => {
                    const item = visibleSelection[virtualItem.index];
                    return (
                      <SelectOption
                        key={String(item.value)}
                        value={String(item.value)}
                        active={selection.includes(item.value)}
                        selected={virtualItem.index === selectedOptionIndex}
                        onClick={() => onOptionSubmit(virtualItem.index)}
                        checkbox
                        label={item.label ?? String(item.value)}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: virtualItem.size,
                          transform: `translateY(${virtualItem.start + OPTION_LIST_PADDING}px)`,
                        }}
                      />
                    );
                  })}
                </div>
              </ScrollArea.Autosize>
            ) : (
              <Combobox.Empty>
                {Array.isArray(data) && data.length > 0
                  ? t('app.select.noOptionsFound')
                  : t('app.select.noOptionsAvailable')}
              </Combobox.Empty>
            )}
          </Combobox.Options>
          {visibleSelection.length >= selectAllMinItems &&
            visibleSelection.length <= selectAllMaxItems && (
              <Combobox.Footer
                data-testid="select-all"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  const modeRemove = !visibleSelection.some(
                    (item) => !selection.includes(item.value)
                  );
                  if (modeRemove) {
                    const toRemove = new Set(
                      visibleSelection.map((item) => item.value)
                    );
                    const nextSelection = selection.filter(
                      (value) => !toRemove.has(value)
                    );
                    setSelection(nextSelection);
                    return;
                  }

                  const nextSelection = [...selection];
                  for (const item of visibleSelection) {
                    if (!nextSelection.includes(item.value)) {
                      nextSelection.push(item.value);
                    }
                  }
                  setSelection(nextSelection);
                }}
              >
                <Checkbox
                  checked={
                    !visibleSelection.some(
                      (item) => !selection.includes(item.value)
                    )
                  }
                  label={t('app.select.selectAbove')}
                  style={{ pointerEvents: 'none' }}
                />
              </Combobox.Footer>
            )}
        </Combobox.Dropdown>
      </Combobox>
    </div>
  );
}
